export interface HostnameTransformConfig {
  companyPrefix: string;
  environmentSuffixes: Record<string, string>;
  namingConventions: {
    toUpperCase: boolean;
    replaceDashes: boolean;
    replacementChar: string;
  };
}

// Configuration for hostname transformations
export const DEFAULT_TRANSFORM_CONFIG: HostnameTransformConfig = {
  companyPrefix: 'CORP',
  environmentSuffixes: {
    'prod': 'PROD',
    'staging': 'STG', 
    'dev': 'DEV',
    'test': 'TST',
    'qa': 'QA',
    'uat': 'UAT'
  },
  namingConventions: {
    toUpperCase: true,
    replaceDashes: true,
    replacementChar: '_'
  }
};

// ServiceNow CI Class mapping
export const SERVICENOW_CI_CLASSES: Record<string, string> = {
  'cmdb_ci_win_server': 'Windows Server',
  'cmdb_ci_linux_server': 'Linux Server', 
  'cmdb_ci_unix_server': 'Unix Server',
  'cmdb_ci_server': 'Physical Server',  
  'cmdb_ci_vm_instance': 'Virtual Machine',
  'cmdb_ci_web_server': 'Web Application',
  'cmdb_ci_appl': 'Application',
  'cmdb_ci_database': 'Database',
  'cmdb_ci_ip_router': 'Router',
  'cmdb_ci_ip_switch': 'Switch', 
  'cmdb_ci_ip_firewall': 'Firewall',
  'cmdb_ci_lb': 'Load Balancer',
  'cmdb_ci_storage_server': 'Storage',
  'cmdb_ci_container': 'Container',
  'cmdb_ci_web_service': 'API',
  'cmdb_ci_service': 'Service',
  'cmdb_ci_service_account': 'Service Account',
  'cmdb_ci_business_app': 'Business Application',
  'cmdb_ci_network_gear': 'Network Device'
};

// Comprehensive field list for on-premise infrastructure
export const DETAILED_CI_FIELDS = [
  // --- Core CI Identifiers ---
  'sys_id',
  'name',
  'sys_class_name', // e.g., 'cmdb_ci_linux_server'
  'short_description',

  // --- Operational Status & Ownership ---
  'operational_status',
  'install_status',
  'environment', // e.g., 'Production', 'Development'
  'support_group.name',
  'assigned_to.name',

  // --- Technical Specs (Virtual & Physical) ---
  'fqdn',
  'ip_address',
  'os',
  'os_version',
  'cpu_count',
  'ram',
  'disk_space',
  
  // --- On-Premise Physical Hardware Attributes ---
  'manufacturer.name',
  'model_id.name',
  'serial_number',
  'asset_tag',
  
  // --- Virtual Infrastructure (VMware, Hyper-V, etc.) ---
  'virtual',
  'object_id', // VMware MoRef
  'vm_inst_id',
  'vcenter_ref',
  'vcenter_uuid',
  
  // --- Network Configuration ---
  'mac_address',
  'dns_domain',
  'default_gateway',
  
  // --- Relationships & Dependencies ---
  'business_unit.name',
  'company.name',
  'location.name',
  'u_data_center', // Custom field for data center
  'u_rack_location', // Custom field for rack
  
  // --- Cost & Financial ---
  'cost',
  'cost_cc',
  'purchase_date',
  'warranty_expiration',
  
  // --- Additional Metadata ---
  'comments',
  'sys_created_on',
  'sys_updated_on',
  'sys_created_by',
  'sys_updated_by',
  
  // --- Custom fields (common for on-premise) ---
  'u_application_owner',
  'u_backup_schedule',
  'u_patching_group',
  'u_monitoring_status'
];

export const SERVICE_MAPPER_API_BASE = 
  (window as any).ENV_CONFIG?.API_BASE_URL !== undefined
    ? `${(window as any).ENV_CONFIG.API_BASE_URL}/api/servicenow`
    : 'http://localhost:5000/api/servicenow';

// ServiceNow Instance URL - can be configured via ENV_CONFIG or defaults to chevrontest
export const SERVICENOW_INSTANCE_URL = 
  (window as any).ENV_CONFIG?.SERVICENOW_INSTANCE_URL !== undefined
    ? (window as any).ENV_CONFIG.SERVICENOW_INSTANCE_URL
    : 'https://chevrontest.service-now.com';
